import React, { useState, useEffect } from "react";
import moment from "moment";
import AddReturnDetails from "../components/AddReturnDetails";
import { privateAxios } from "../utils/axios";
import { XMarkIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

function ReturnList() {
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [returns, setReturns] = useState([]);
  const [updatePage, setUpdatePage] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    fetchReturnsData();
  }, [updatePage, currentPage, debouncedSearch, startDate, endDate]);

  const fetchReturnsData = () => {
    setIsLoading(true);
    const queryParams = new URLSearchParams({
      page: currentPage,
      limit: 10,
      search: debouncedSearch,
    });

    if (startDate) {
      queryParams.append("startDate", startDate);
    }
    if (endDate) {
      queryParams.append("endDate", endDate);
    }

    privateAxios
      .get(`/returns/?${queryParams}`)
      .then((response) => {
        if (response.data.success) {
          setReturns(response.data.data);
          setTotalPages(response.data.totalPages);
        }
      })
      .catch((err) => console.log(err))
      .finally(() => setIsLoading(false));
  };

  const addReturnModalSetting = () => {
    setShowReturnModal(!showReturnModal);
  };

  const handlePageUpdate = () => {
    setUpdatePage(!updatePage);
  };

  const handleDetailsClick = (returnItem) => {
    setSelectedReturn(returnItem);
    setShowDetailsModal(true);
  };

  const handleClearSearch = () => {
    setSearch("");
    setCurrentPage(1);
  };

  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  const handleDateChange = (type, value) => {
    if (type === "start") {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
    setCurrentPage(1);
  };

  const handleDownloadInvoice = async (returnId) => {
    try {
      const response = await privateAxios.get(
        `/returns/${returnId}/invoice`,
        {
          responseType: "blob",
        },
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `return-invoice-${returnId}.pdf`);

      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success("Invoice downloaded successfully");
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Failed to download invoice");
    }
  };

  const handleDeleteClick = async (returnId) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this return? This action cannot be undone.",
    );

    if (!confirmed) return;

    try {
      await privateAxios.delete(`/returns/${returnId}`);

      toast.success("Return deleted successfully");

      await fetchReturnsData();
    } catch (error) {
      console.error("Error deleting return:", error);
      toast.error(error?.response?.data?.message);
    }
  };

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center">
      <div className="flex flex-col gap-5 w-11/12">
        {/* Overall Returns Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 w-full mt-6 mb-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <span className="font-bold text-xl text-gray-900">
                Overall Returns
              </span>
              <div className="text-gray-500 text-sm mt-1">
                Overview of your returns status
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 bg-gray-50 rounded-lg flex items-center gap-4 p-5">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-100">
                <svg
                  className="w-6 h-6 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div>
                <div className="text-gray-500 text-sm">Total Returns</div>
                <div className="font-bold text-2xl text-gray-900">
                  {returns.length}
                </div>
              </div>
            </div>
          </div>
        </div>

        {showReturnModal && (
          <AddReturnDetails
            addReturnModalSetting={addReturnModalSetting}
            handlePageUpdate={handlePageUpdate}
          />
        )}

        {showDetailsModal && selectedReturn && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white w-full max-w-5xl h-[92vh] flex flex-col shadow-xl border border-gray-200">
              <div className="bg-slate-800 text-white px-8 py-4 flex justify-between items-center flex-shrink-0">
                <div>
                  <h2 className="text-lg font-bold tracking-wide uppercase">Return Invoice</h2>
                  <p className="text-slate-400 text-xs mt-0.5">Invoice No: {selectedReturn?.invoiceNumber}</p>
                </div>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="overflow-y-auto flex-1 px-8 py-6 bg-gray-50">
                <div className="grid grid-cols-2 gap-0 mb-6 border border-gray-300 bg-white">
                  <div className="p-5 border-r border-gray-300">
                    <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-3 pb-2 border-b border-gray-200">
                      Receiver Information
                    </div>
                    <table className="w-full text-sm">
                      <tbody>
                        <tr>
                          <td className="py-1 text-gray-500 w-36">Receiver Name</td>
                          <td className="py-1 font-medium text-gray-800">: {selectedReturn?.receiverName}</td>
                        </tr>
                        <tr>
                          <td className="py-1 text-gray-500">Receiver Number</td>
                          <td className="py-1 font-medium text-gray-800">: {selectedReturn?.receiverPhone}</td>
                        </tr>
                        <tr>
                          <td className="py-1 text-gray-500">Receiver Address</td>
                          <td className="py-1 font-medium text-gray-800">: {selectedReturn?.receiverAddress}</td>
                        </tr>
                        <tr>
                          <td className="py-1 text-gray-500">Reason</td>
                          <td className="py-1 font-medium text-gray-800">: {selectedReturn?.reason}</td>
                        </tr>
                        <tr>
                          <td className="py-1 text-gray-500">Ref No</td>
                          <td className="py-1 font-medium text-gray-800">: {selectedReturn?.ref}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="p-5">
                    <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-3 pb-2 border-b border-gray-200">
                      Invoice Details
                    </div>
                    <table className="w-full text-sm">
                      <tbody>
                        <tr>
                          <td className="py-1 text-gray-500 w-36">Invoice No</td>
                          <td className="py-1 font-medium text-gray-800">: {selectedReturn?.invoiceNumber}</td>
                        </tr>
                        <tr>
                          <td className="py-1 text-gray-500">Transport No</td>
                          <td className="py-1 font-medium text-gray-800">: {selectedReturn?.transportNumber}</td>
                        </tr>
                        <tr>
                          <td className="py-1 text-gray-500">Driver Name</td>
                          <td className="py-1 font-medium text-gray-800">: {selectedReturn?.driverName}</td>
                        </tr>
                        <tr>
                          <td className="py-1 text-gray-500">Driver Number</td>
                          <td className="py-1 font-medium text-gray-800">: {selectedReturn?.driverPhone}</td>
                        </tr>
                        <tr>
                          <td className="py-1 text-gray-500">Driver Address</td>
                          <td className="py-1 font-medium text-gray-800">: {selectedReturn?.driverAddress}</td>
                        </tr>
                        <tr>
                          <td className="py-1 text-gray-500">Return Date</td>
                          <td className="py-1 font-medium text-gray-800">: {moment(selectedReturn?.returnDate).format("DD/MM/YYYY, h:mm A")}</td>
                        </tr>
                        <tr>
                          <td className="py-1 text-gray-500">Prepared By</td>
                          <td className="py-1 font-medium text-gray-800">: {selectedReturn?.createBy?.name}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="border border-gray-300 bg-white">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500 px-5 py-3 border-b border-gray-300">
                    Products
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-700 text-white text-xs uppercase tracking-wide">
                        <th className="px-4 py-3 text-center font-semibold w-10">SL</th>
                        <th className="px-4 py-3 text-left font-semibold">Product Description</th>
                        <th className="px-4 py-3 text-center font-semibold">Category</th>
                        <th className="px-4 py-3 text-center font-semibold">Unit</th>
                        <th className="px-4 py-3 text-center font-semibold">Qty</th>
                        <th className="px-4 py-3 text-right font-semibold">Unit Price</th>
                        <th className="px-4 py-3 text-right font-semibold">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {selectedReturn?.products?.map((product, index) => (
                        <tr key={index} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 text-center text-gray-400 text-xs">{index + 1}</td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-gray-900 text-sm">{product.product.name}</div>
                            {product?.skus?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5 items-center">
                                <span className="text-[11px] text-gray-400 font-medium">S/N:</span>
                                {product?.skus?.map((sku, index) => (
                                  <span
                                    key={index}
                                    className="px-1.5 py-0.5 text-[11px] bg-slate-100 text-slate-600 border border-slate-200 font-mono"
                                  >
                                    {sku}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center text-gray-600 text-xs">{product?.product?.category}</td>
                          <td className="px-4 py-3 text-center text-gray-600 text-xs">{product?.product?.unit}</td>
                          <td className="px-4 py-3 text-center font-semibold text-gray-800">{product?.quantity}</td>
                          <td className="px-4 py-3 text-right text-gray-600">৳{product?.price}</td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-900">৳{product?.price * product?.quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="flex justify-end border-t border-gray-200">
                    <div className="w-72">
                      <div className="flex justify-between px-5 py-2.5 text-sm border-b border-gray-100">
                        <span className="text-gray-500">Sub Total</span>
                        <span className="font-medium text-gray-700">৳{selectedReturn.subTotal}</span>
                      </div>
                      <div className="flex justify-between px-5 py-3 bg-slate-800 text-white">
                        <span className="font-semibold text-sm">Total Amount</span>
                        <span className="font-bold text-base">৳{selectedReturn.totalAmount}</span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto rounded-lg border bg-white border-gray-200 mb-10">
          <div className="flex justify-between pt-5 pb-3 px-3 flex-col md:flex-row md:items-center my-3">
            <div className="flex gap-4 justify-center items-center w-full md:w-auto mb-4 md:mb-0">
              <span className="font-bold text-lg">Return List</span>
              <div className="relative flex items-center px-2 border-2 rounded-md shadow-sm w-80 bg-white">
                <img
                  alt="search-icon"
                  className="w-5 h-5"
                  src={require("../assets/search-icon.png")}
                />
                <input
                  className="border-none outline-none focus:outline-none focus:ring-0 focus:border-none text-base pl-2 pr-6 bg-transparent w-full"
                  type="text"
                  placeholder="Search here"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    type="button"
                    className="absolute right-2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    onClick={handleClearSearch}
                    tabIndex={-1}
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex gap-2 items-center">
                <input
                  type="date"
                  className="border rounded px-2 py-1 text-base"
                  value={startDate}
                  onChange={(e) => handleDateChange("start", e.target.value)}
                />
                <span>to</span>
                <input
                  type="date"
                  className="border rounded px-2 py-1 text-base"
                  value={endDate}
                  onChange={(e) => handleDateChange("end", e.target.value)}
                />
                {(startDate || endDate) && (
                  <button
                    onClick={handleClearDates}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex gap-4 justify-end w-full md:w-auto">
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-2 text-base rounded-lg transition-all duration-200 min-w-[160px]"
                onClick={addReturnModalSetting}
              >
                Add Return
              </button>
            </div>
          </div>

          <table className="min-w-full divide-y divide-gray-200 text-sm overflow-hidden shadow-sm border">
            <thead>
              <tr className="bg-gray-200 border-b">
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[8%] text-sm">
                  S.No
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[15%] text-sm">
                  Invoice Number
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[15%] text-sm">
                  Receiver Name
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[15%] text-sm">
                  Driver Name
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[15%] text-sm">
                  Transport Number
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[12%] text-sm">
                  Return Date
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[10%] text-sm">
                  Total Amount
                </th>
                <th className="whitespace-nowrap px-4 py-3 text-left font-semibold text-gray-900 w-[9%] text-sm">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                      <span className="ml-2 text-sm text-gray-500">
                        Loading returns...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : returns.length === 0 ? (
                <tr>
                  <td
                    colSpan="8"
                    className="px-4 py-8 text-center text-sm text-gray-500"
                  >
                    No returns found
                  </td>
                </tr>
              ) : (
                returns.map((returnItem, index) => (
                  <tr
                    key={returnItem._id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      {index + 1}
                    </td>

                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      {returnItem.invoiceNumber}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-900 capitalize truncate max-w-[200px] text-sm">
                      {returnItem.receiverName}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 capitalize truncate max-w-[200px] text-sm">
                      {returnItem.driverName}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 capitalize truncate max-w-[200px] text-sm">
                      {returnItem.transportNumber}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      {new Date(returnItem.returnDate).toLocaleDateString()}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700 text-sm">
                      ৳{returnItem.totalAmount}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDetailsClick(returnItem)}
                          className="bg-blue-500 hover:bg-blue-600 text-white font-medium px-2.5 py-0.5 rounded text-xs transition-colors duration-200"
                        >
                          Details
                        </button>
                        <button
                          onClick={() => handleDownloadInvoice(returnItem._id)}
                          className="bg-green-500 hover:bg-green-600 text-white font-medium px-2.5 py-0.5 rounded text-xs transition-colors duration-200"
                        >
                          Invoice
                        </button>

                        <button
                          onClick={() => handleDeleteClick(returnItem._id)}
                          className="bg-red-500 hover:bg-red-600 text-white font-medium px-2.5 py-0.5 rounded text-xs transition-colors duration-200"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {!isLoading && (
            <div className="flex justify-end items-center gap-4 py-5 pr-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  className="px-4 py-2 rounded border text-base disabled:opacity-50 hover:bg-gray-50 transition-all duration-200"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-base">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) =>
                      prev < totalPages ? prev + 1 : prev,
                    )
                  }
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 rounded border text-base disabled:opacity-50 hover:bg-gray-50 transition-all duration-200"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReturnList;
